import React, { useEffect, useRef, useContext } from 'react';
import '/public/css/pages/collection.css';
import '/public/css/components/apmGallerySlideshow.css';
import { Pagination, Figure } from '@apmg/titan';
import { Body } from '@apmg/amat';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Metatags from '../../components/Metatags/Metatags';
import AudioPlayButton from '../../components/AudioPlayButton/AudioPlayButton';
import Byline from '../../components/Byline/Byline';
import { fishForSocialMediaImage } from '../../components/Metatags/MetaTagHelpers';
import AdCategoriesContext from 'context/AdCategoriesContext';
import { format, parseISO } from 'date-fns';
import LiveStyles from '../../styles/modules/Live.module.css';
import Link from 'components/AppSafeLink/AppSafeLink';
import IconChevron from '../../components/Icons/IconChevron';
import { Image } from '@apmg/mimas';
import { getVideoEmbedUrl } from '../../utils/utils';
import { linkByTypeAs } from '../../utils/cjsutils';
import { audioDownloadPrefix } from '../../utils/utils';
import { escapeJsonLd } from '../../utils/jsonLd';
import Script from 'next/script';

import '/public/css/pages/weatherLive.css';

const Collection = ({ data: { collection }, withinPage }) => {
  const context = useContext(AdCategoriesContext);
  const router = useRouter();
  const img = fishForSocialMediaImage(collection);
  const contentTopicCollectionRef = useRef(null);
  const collectionItemsRef = useRef(null);

  let checkCollectionName = `${collection?.title}`;

  if (!checkCollectionName) {
    checkCollectionName = 'default';
  }

  // Check if current URL path is weather-live
  const isWeatherLive = router.asPath.includes('/weather-live');

  const noFollowIndex = collection?.results?.currentPage > 1;

  useEffect(() => {
    context.setPrimaryCategory(collection.canonicalSlug);
    context.setAdCategories([collection.canonicalSlug]);
    if (contentTopicCollectionRef) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'sendUWContentTopic',
        contentTopic: checkCollectionName,
      });
    } else {
      console.error('you broke the ads');
    }
  }, [collection.canonicalSlug]);

  // Scroll to top of content when page changes
  useEffect(() => {
    const handleRouteChange = () => {
      if (collectionItemsRef.current) {
        setTimeout(() => {
          collectionItemsRef.current.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const collectionImageData = collection.primaryVisuals.lead
    ? collection.primaryVisuals.lead
    : collection.primaryVisuals.social;

  const liveBlogUpdates = (collection.results?.items ?? [])
    .filter((item) => item.publishDate)
    .map((item) => ({
      '@type': 'BlogPosting',
      headline: item.title,
      datePublished: item.publishDate,
      articleBody: item.descriptionText,
      url: `https://www.mprnews.org/${item.canonicalSlug}`,
    }));

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'LiveBlogPosting',
    '@id': `https://www.mprnews.org/${collection.canonicalSlug}`,
    headline: collection.title,
    description: collection.descriptionText,
    coverageStartTime: collection.publishDate,
    liveBlogUpdate: liveBlogUpdates,
  };
  return (
    <>
      {!withinPage && (
        <Metatags
          title={collection.title}
          fullSlug={collection.canonicalSlug}
          description={collection.descriptionText}
          image={img?.url}
          imageHeight={img?.height}
          imageWidth={img?.width}
          imageAlt={collection?.primaryVisuals?.social?.shortCaption}
          topic={collection?.title}
          contentType="website"
          rssUrl={collection.rssUrl}
          originalSourceUrl={collection.originalSourceUrl}
          noFollow={noFollowIndex}
        />
      )}
      {!noFollowIndex && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: escapeJsonLd(schemaData),
          }}
        />
      )}
      {isWeatherLive && (
        <section id="radar" className="section">
          <Script src="https://widgets.media.weather.com/wxwidget.loader.js?cid=431513429" />
          <h2 className="hdg-3">Radar</h2>
          <wx-config
            latitude="44.8848"
            longitude="-93.2223"
            group="true"
          ></wx-config>
          <wx-widget
            type="map"
            mapid="0001"
            group="true"
            zoomlevel="8"
          ></wx-widget>
        </section>
      )}
      <section
        className={`page-purpose ${LiveStyles.liveupdates}`}
        data-mpr-content-topic={collection.title}
        ref={contentTopicCollectionRef}
      >
        {collection.primaryVisuals.video ? (
          <div className="teaser-primary-visual">
            <iframe
              allowFullScreen
              loading="lazy"
              title={collection.primaryVisuals.video.url}
              src={getVideoEmbedUrl(collection.primaryVisuals.video.url)}
            ></iframe>
          </div>
        ) : collectionImageData ? (
          <div>
            <Figure
              caption={collectionImageData?.longCaption}
              credit={collectionImageData?.credit?.name}
              creditHref={collectionImageData?.credit?.url}
              elementClass={'content_figure'}
              image={
                <Image
                  image={collectionImageData}
                  elementclassName="content_thumbnail"
                  aspectRatio="widescreen"
                  sizes={`100vw`}
                  alt={collectionImageData?.shortCaption || collection.title}
                  loading="lazy"
                />
              }
            />
          </div>
        ) : null}

        <div className="collection collection-1col">
          {collection?.body && !withinPage && (
            <div className="collection-body userContent">
              <Body
                nodeData={JSON.parse(collection.body)}
                embedded={collection.embeddedAssets}
              />
            </div>
          )}
          <div
            className={`collection_items ${LiveStyles.liveupdates}`}
            ref={collectionItemsRef}
          >
            {collection.results.items.map((item) => {
              const imageData = item.primaryVisuals.lead
                ? item.primaryVisuals.lead
                : item.primaryVisuals.thumbnail;

              const hasImage = imageData ? 'hasImage' : '';

              // Format contributors for Byline component
              let authors = [];
              if (item.contributors && item.contributors.length > 0) {
                authors = item.contributors.map((contributor) => ({
                  title:
                    `${contributor.profile?.firstName || ''} ${
                      contributor.profile?.lastName || ''
                    }`.trim() || contributor.profile?.title,
                  href: `/people/[...slug]`,
                  as: `/people/${contributor.profile?.canonicalSlug}`,
                  order: contributor.order,
                }));
              }

              return (
                <article
                  className={LiveStyles.article}
                  key={item.canonicalSlug}
                >
                  {item.primaryVisuals.video ? (
                    <div className={LiveStyles.teaser}>
                      <iframe
                        allowFullScreen
                        loading="lazy"
                        title={item.primaryVisuals.video.url}
                        src={getVideoEmbedUrl(item.primaryVisuals.video.url)}
                      ></iframe>
                    </div>
                  ) : imageData ? (
                    <div className={LiveStyles.image}>
                      <Figure
                        caption={imageData?.longCaption}
                        credit={imageData?.credit?.name}
                        creditHref={imageData?.credit?.url}
                        elementClass={'content_figure'}
                        image={
                          <Link
                            passHref
                            data-testid="relativeLink"
                            href={linkByTypeAs(item)}
                          >
                            <Image
                              image={imageData}
                              elementclassName="content_thumbnail"
                              aspectRatio="widescreen"
                              sizes={`100vw`}
                              alt={imageData?.shortCaption || item.title}
                              loading="lazy"
                            />
                          </Link>
                        }
                      />
                    </div>
                  ) : null}

                  <div className="teaser_content">
                    <span className={`${LiveStyles.liveHeader}`}>
                      <h3
                        className={`hdg hdg-3 ${LiveStyles.liveTitle} ${
                          hasImage ? LiveStyles.hasImage : ''
                        }`}
                      >
                        <Link href={linkByTypeAs(item)}>{item.title}</Link>
                      </h3>
                      <span className={LiveStyles.liveMeta}>
                        {authors.length > 0 && (
                          <span
                            className={LiveStyles.liveByline}
                            data-testid="contentByline"
                          >
                            <Byline authors={authors} />
                          </span>
                        )}
                        {item.dateline && (
                          <span className={LiveStyles.liveDateline}>
                            {item.dateline}{' '}
                          </span>
                        )}
                        {item.publishDate && (
                          <span className={LiveStyles.livePubdate}>
                            {/* Titan component <Time/> would typically be used here 
                            but it is unable to format time in the AP style ( a.m. / p.m.)
                            so we are using date-fns to format the time instead.
                             */}
                            <time dateTime={item.publishDate}>
                              {format(
                                parseISO(item.publishDate),
                                'h:mm aaaa MMM. d'
                              )}
                            </time>
                          </span>
                        )}
                        {item.updatedPublishDate && (
                          <span className={LiveStyles.liveUpdated}>
                            <strong>Updated: </strong>
                            {/* See above comment on date time library choice */}
                            <time dateTime={item.updatedPublishDate}>
                              {format(
                                parseISO(item.updatedPublishDate),
                                'h:mm aaaa MMM. d'
                              )}
                            </time>
                          </span>
                        )}
                      </span>
                    </span>
                    {item?.audio?.[0]?.encodings?.[0]?.playFilePath && (
                      <div className="teaser-controls">
                        <div className="teaser_button">
                          <AudioPlayButton
                            playBtnSm={true}
                            label="Listen"
                            audioTitle={item.title}
                            audioSource={audioDownloadPrefix(
                              item.audio[0].encodings[0].playFilePath
                            )}
                            audioThumbnailSrc={
                              item.primaryVisuals.thumbnail?.aspect_ratios
                                ?.square?.instances[0]
                            }
                            audioThumbnailAlt={
                              item.primaryVisuals.thumbnail?.shortCaption ||
                              item.title
                            }
                            showDuration={`${
                              item.audio[0]?.encodings[0]?.durationHms ?? ''
                            }`}
                          />
                        </div>
                      </div>
                    )}

                    {item.body && (
                      <div className={LiveStyles.body}>
                        <Body
                          nodeData={JSON.parse(item.body)}
                          embedded={item.embeddedAssets}
                        />
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
          <div className="collection-pagination">
            <div className="pagination_container type-sm">
              {collection.results.totalItems > 10 && (
                <Pagination
                  hasFirstAndLast={true}
                  inclusiveFirstLast={false}
                  buffer={2}
                  firstLastSeparator="..."
                  hrefPrefix={`[...slug]`}
                  asPrefix={`${collection?.canonicalSlug}`}
                  currentPage={collection.results.currentPage}
                  totalPages={collection.results.totalPages}
                  totalItems={collection.results.totalItems}
                  firstSymbol={
                    <>
                      <IconChevron
                        direction="left"
                        color="currentColor"
                        decorative
                      />
                      <IconChevron
                        direction="left"
                        color="currentColor"
                        decorative
                      />
                    </>
                  }
                  lastSymbol={
                    <>
                      <IconChevron
                        direction="right"
                        color="currentColor"
                        decorative
                      />
                      <IconChevron
                        direction="right"
                        color="currentColor"
                        decorative
                      />
                    </>
                  }
                  nextSymbol={
                    <>
                      <span>Older</span>
                      <IconChevron
                        direction="right"
                        color="currentColor"
                        decorative
                      />
                    </>
                  }
                  prevSymbol={
                    <>
                      <IconChevron
                        direction="left"
                        color="currentColor"
                        decorative
                      />
                      <span aria-label="Previous">Newer</span>
                    </>
                  }
                  prevNextClass="btn btn-primary btn-pagination no-text-transform"
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

Collection.propTypes = {
  data: PropTypes.shape({
    collection: PropTypes.object,
  }),
  withinPage: PropTypes.bool,
};

export default Collection;
